/* ============================================================
   BLUE SALON — SHOE SIZE SWITCHER (EU / US / UK)
   Cole Haan PDPs only (see size_system_on in sections/product.liquid).
   Variants are stored in US sizes and stay that way: the chips'
   data-opt-value, the variant id, ?variant= and everything sent to the
   cart are never touched. This file only swaps visible text (and the
   matching aria-labels) between the three systems.

   Liquid prints every chip in EU with data-size-us / -eu / -uk
   (snippets/size-system-convert.liquid), so the page paints in EU with
   no flash of US numbers. Labels are always read from data-size-us or
   those attributes — never from the text on screen, which would
   compound rounding.

   assets/product.js rewrites each chip's aria-label and every
   [data-opt-out] readout with the raw US value whenever a pick changes,
   so a MutationObserver on the scope puts the active system back. Its
   own writes only happen when a value differs, so it settles after one
   extra pass.
   ============================================================ */
(function () {
  "use strict";

  /* US size → EU / UK. Same data as snippets/size-system-convert.liquid
     and the size guide's sg_brand_sizes — change all three together. */
  var SIZE_TABLES = {
    men: {
      eu: { 6: 39, 6.5: 39.5, 7: 40, 7.5: 40.5, 8: 41, 8.5: 41.5, 9: 42, 9.5: 42.5, 10: 43, 10.5: 43.5, 11: 44, 11.5: 44.5, 12: 45, 12.5: 45.5, 13: 46, 14: 47, 15: 48, 16: 49 },
      uk: { 6: 5, 6.5: 5.5, 7: 6, 7.5: 6.5, 8: 7, 8.5: 7.5, 9: 8, 9.5: 8.5, 10: 9, 10.5: 9.5, 11: 10, 11.5: 10.5, 12: 11, 12.5: 11.5, 13: 12, 14: 13, 15: 14, 16: 15 }
    },
    women: {
      eu: { 4: 34, 4.5: 34.5, 5: 35, 5.5: 35.5, 6: 36, 6.5: 37, 7: 37.5, 7.5: 38, 8: 38.5, 8.5: 39, 9: 39.5, 9.5: 40, 10: 40.5, 10.5: 41, 11: 42, 11.5: 42.5, 12: 43 },
      uk: { 4: 1.5, 4.5: 2, 5: 2.5, 5.5: 3, 6: 3.5, 6.5: 4, 7: 4.5, 7.5: 5, 8: 5.5, 8.5: 6, 9: 6.5, 9.5: 7, 10: 7.5, 10.5: 8, 11: 8.5, 11.5: 9, 12: 9.5 }
    }
  };

  var SYSTEMS = ["eu", "us", "uk"];
  var DEFAULT_SYSTEM = "eu";
  // Mirrors default_gender / unisex_gender in snippets/size-system-gender.liquid.
  var DEFAULT_GENDER = "women";
  var UNISEX_GENDER = ""; // "" hides the switcher on unisex products; "men" uses the men's table
  var STORAGE_KEY = "sizeSystem";

  var WOMEN_VALUES = ["women", "womens", "women's", "female", "ladies"];
  var MEN_VALUES = ["men", "mens", "men's", "male"];
  var WOMEN_TAGS = ["women", "womens", "women's", "female"];
  var MEN_TAGS = MEN_VALUES;

  /* ── Parsing / conversion ─────────────────────────────────── */

  // "8", "8.0", "8,5", "8.5 W", "10 D", "9 Wide" → { num, suffix }.
  // null when there's no leading number (S / M / L, "One Size").
  function parseSize(value) {
    var raw = String(value == null ? "" : value).trim();
    var match = raw.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
    if (!match) return null;
    var num = parseFloat(match[1].replace(",", "."));
    if (!isFinite(num)) return null;
    return { num: num, suffix: match[2].trim() };
  }

  // 40 → "40", 40.5 → "40.5" (never "40.0").
  function formatSize(num) {
    return String(Math.round(num * 100) / 100);
  }

  // A US value in the chosen system. Anything not in the table comes back
  // as the original US value, so a chip never reads blank or "NaN".
  function convertSize(value, system, gender) {
    var original = String(value == null ? "" : value).trim();
    if (system === "us") return original;
    var tables = SIZE_TABLES[gender];
    var table = tables && tables[system];
    var parsed = parseSize(original);
    if (!table || !parsed) return original;
    var converted = table[String(parsed.num)];
    if (converted == null) return original;
    return formatSize(converted) + (parsed.suffix ? " " + parsed.suffix : "");
  }

  /* ── Gender ───────────────────────────────────────────────── */

  // Same order as snippets/size-system-gender.liquid: metafield, exact
  // tag, product type, collection handles, default. "women" is always
  // tested before "men", which it contains. null = hide the switcher.
  function detectGender(info) {
    info = info || {};
    var norm = function (s) { return String(s == null ? "" : s).toLowerCase().trim(); };

    var meta = norm(info.gender);
    if (WOMEN_VALUES.indexOf(meta) !== -1) return "women";
    if (MEN_VALUES.indexOf(meta) !== -1) return "men";
    if (meta === "unisex") return UNISEX_GENDER || null;

    var tags = (info.tags || []).map(norm);
    var hasTag = function (list) { return tags.some(function (t) { return list.indexOf(t) !== -1; }); };
    if (hasTag(WOMEN_TAGS)) return "women";
    if (hasTag(MEN_TAGS)) return "men";
    if (tags.indexOf("unisex") !== -1) return UNISEX_GENDER || null;

    var type = norm(info.type);
    if (type.indexOf("women") !== -1) return "women";
    if (type.indexOf("men") !== -1) return "men";

    var handles = (info.collections || []).map(norm).join(" ");
    if (handles.indexOf("women") !== -1) return "women";
    if (handles.indexOf("men") !== -1) return "men";

    return DEFAULT_GENDER;
  }

  function splitList(str) {
    return String(str || "").split(",").filter(function (s) { return s.trim() !== ""; });
  }

  // Liquid normally decides and prints data-size-gender; the data-product-*
  // attributes are the fallback for a scope rendered without it.
  function scopeGender(scope) {
    var ds = scope.dataset;
    if (ds.sizeGender) return SIZE_TABLES[ds.sizeGender] ? ds.sizeGender : null;
    return detectGender({
      gender: ds.productGender,
      tags: splitList(ds.productTags),
      type: ds.productType,
      collections: splitList(ds.productCollections)
    });
  }

  /* ── Remembered choice ────────────────────────────────────── */

  function readStored() {
    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      return SYSTEMS.indexOf(stored) !== -1 ? stored : null;
    } catch (e) {
      return null;
    }
  }

  function store(system) {
    try { window.localStorage.setItem(STORAGE_KEY, system); } catch (e) {}
  }

  /* ── DOM ──────────────────────────────────────────────────── */

  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  // Server-rendered label first; computed from the US value otherwise.
  function chipLabel(chip, system, gender) {
    var key = "size" + system.charAt(0).toUpperCase() + system.slice(1);
    var printed = chip.dataset[key];
    return printed != null && printed !== "" ? printed : convertSize(chip.dataset.sizeUs, system, gender);
  }

  function apply(scope, system, gender) {
    var code = system.toUpperCase();
    var soldOut = scope.dataset.sizeSoldout || "(Sold out)";

    scope.querySelectorAll(".size-system__btn").forEach(function (btn) {
      var on = btn.dataset.system === system;
      btn.classList.toggle("is-active", on);
      setAttr(btn, "aria-pressed", on ? "true" : "false");
    });

    scope.querySelectorAll("[data-size-us]").forEach(function (chip) {
      var label = chipLabel(chip, system, gender);
      setText(chip, label);
      var group = chip.parentElement && chip.parentElement.getAttribute("aria-label");
      setAttr(chip, "aria-label", (group ? group + " " : "") + code + " " + label +
        (chip.classList.contains("is-soldout") ? " " + soldOut : ""));
    });

    // Readouts follow whichever chip is picked. With nothing picked they
    // keep product.js's own placeholder ("Select" / "—").
    var active = scope.querySelector("[data-size-us].is-active");
    if (active) {
      var activeLabel = chipLabel(active, system, gender);
      scope.querySelectorAll("[data-size-readout]").forEach(function (el) {
        setText(el, activeLabel);
      });
    }
  }

  var WATCHED = "[data-size-us], [data-size-readout]";

  function touchesSizes(records) {
    return records.some(function (record) {
      var target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (target && target.closest(WATCHED)) return true;
      for (var i = 0; i < record.addedNodes.length; i++) {
        var node = record.addedNodes[i];
        if (node.nodeType === 1 && (node.matches(WATCHED) || node.querySelector(WATCHED))) return true;
      }
      return false;
    });
  }

  function initScope(scope) {
    if (scope.getAttribute("data-size-switcher-init") === "true") return;
    scope.setAttribute("data-size-switcher-init", "true");

    var gender = scopeGender(scope);
    if (!gender) {
      scope.querySelectorAll(".size-system").forEach(function (el) { el.hidden = true; });
      return;
    }

    var system = readStored() || DEFAULT_SYSTEM;
    apply(scope, system, gender);

    scope.addEventListener("click", function (e) {
      var btn = e.target.closest(".size-system__btn");
      if (!btn || !scope.contains(btn) || SYSTEMS.indexOf(btn.dataset.system) === -1) return;
      system = btn.dataset.system;
      store(system);
      apply(scope, system, gender);
    });

    // Picks, availability re-bakes and re-rendered pickers all land here.
    if ("MutationObserver" in window) {
      new MutationObserver(function (records) {
        if (touchesSizes(records)) apply(scope, system, gender);
      }).observe(scope, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["class", "aria-label"]
      });
    }
  }

  function init(root) {
    root = root || document;
    if (root.matches && root.matches("[data-size-switcher-scope]")) initScope(root);
    root.querySelectorAll("[data-size-switcher-scope]").forEach(initScope);
  }

  window.BSSizeSwitcher = {
    SIZE_TABLES: SIZE_TABLES,
    parseSize: parseSize,
    formatSize: formatSize,
    convertSize: convertSize,
    detectGender: detectGender,
    init: init
  };

  init(document);
  // Theme editor re-renders a section in place; the new markup needs binding.
  document.addEventListener("shopify:section:load", function (e) { init(e.target); });
})();
